import type { AuthError, User } from '@supabase/supabase-js'
import { supabase } from '@/shared/supabase/client'
import type {
  PasskeyCreationOptionsJSON,
  PasskeyRegistrationResponseJSON,
} from '@/shared/lib/platform/passkeyCeremony'

/** Shape returned by Supabase passkey APIs (snake_case). */
export type PasskeySdkItem = {
  id: string
  friendly_name?: string
  created_at: string
  last_used_at?: string
}

type PasskeyAuthResult<T> = Promise<{ data: T | null; error: AuthError | null }>

export type PasskeyRegistrationOptionsPayload = {
  challenge_id: string
  options: PasskeyCreationOptionsJSON
  expires_at: number
}

/** Passkey auth surface enabled via `experimental.passkey` in createClient options. */
export interface PasskeySupabaseAuth {
  registerPasskey(): PasskeyAuthResult<PasskeySdkItem>
  signInWithPasskey(): PasskeyAuthResult<{ user: User }>
  passkey: {
    startRegistration(): PasskeyAuthResult<PasskeyRegistrationOptionsPayload>
    verifyRegistration(args: {
      challengeId: string
      credential: PasskeyRegistrationResponseJSON
    }): PasskeyAuthResult<PasskeySdkItem>
    list(): PasskeyAuthResult<PasskeySdkItem[]>
    update(args: { passkeyId: string; friendlyName: string }): PasskeyAuthResult<unknown>
    delete(args: { passkeyId: string }): PasskeyAuthResult<unknown>
  }
}

/** Typed accessor — passkey methods exist at runtime but are not yet in @supabase/supabase-js types. */
export const passkeyAuth = (): PasskeySupabaseAuth =>
  supabase.auth as unknown as PasskeySupabaseAuth
